export interface IfcDepth {
  openings: Array<Record<string, unknown>>;
  materials: Array<Record<string, unknown>>;
  rooms_with_polygons: Array<Record<string, unknown>>;
  building_footprint_polygon: unknown[];
  extraction_warnings: string[];
}

export const EMPTY_IFC: IfcDepth = {
  openings: [],
  materials: [],
  rooms_with_polygons: [],
  building_footprint_polygon: [],
  extraction_warnings: [],
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function recordArray(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

function arrayValue(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Map the backend's canonical ``ExtractionResult.to_dict()`` response into
 * the Step-2/docgen IFC-depth fields. Older/future top-level aliases win,
 * but today's source of truth is ``rooms`` and ``geometry_preview``.
 */
export function extractIfcDepth(raw: Record<string, unknown>): IfcDepth {
  const geometryPreview = isRecord(raw.geometry_preview) ? raw.geometry_preview : {};
  const topLevelRooms = recordArray(raw.rooms_with_polygons);
  const canonicalRooms = recordArray(raw.rooms);
  const previewRooms = recordArray(geometryPreview.rooms);
  const topLevelFootprint = arrayValue(raw.building_footprint_polygon);
  const previewFootprint = arrayValue(geometryPreview.building_footprint);

  return {
    openings: recordArray(raw.openings),
    materials: recordArray(raw.materials),
    rooms_with_polygons: topLevelRooms.length ? topLevelRooms : canonicalRooms.length ? canonicalRooms : previewRooms,
    building_footprint_polygon: topLevelFootprint.length ? topLevelFootprint : previewFootprint,
    extraction_warnings: stringArray(raw.warnings),
  };
}
